import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { MTO_ALGORITHMS_API } from '../../../../../config';
import { DEFAULT_ALGORITHMS } from '../constants';

export const useAlgorithms = (demand = 0) => {
  const [allAlgorithms, setAllAlgorithms] = useState(DEFAULT_ALGORITHMS);
  const [isLoadingAlgorithms, setLoadingAlgorithms] = useState(true);
  const [selectedAlgorithm, setSelectedAlgorithm] = useState('connection_shortest_path');
  const [error, setError] = useState('');
  
  // Filter algorithms based on demand (capacity constraint enabled or not)
  const algorithms = useMemo(() => {
    const filtered = {};
    Object.entries(allAlgorithms).forEach(([key, info]) => {
      const requiresDemand = info.requires_demand === true;
      const isCapacityEnabled = demand > 0;
      
      // Show algorithm if:
      // - It requires demand and demand > 0 (capacity constraint enabled)
      // - It doesn't require demand and demand = 0 (standard mode)
      if (requiresDemand === isCapacityEnabled) {
        filtered[key] = info;
      }
    });
    return filtered;
  }, [allAlgorithms, demand]);

  // Load algorithms on mount
  useEffect(() => {
    loadAlgorithms();
  }, []);

  // Auto-switch algorithm when demand changes
  useEffect(() => {
    const availableAlgorithmKeys = Object.keys(algorithms);
    
    // If no algorithms available, do nothing
    if (availableAlgorithmKeys.length === 0) {
      return;
    }
    
    // If current algorithm is not available in filtered list, switch to appropriate one
    if (!availableAlgorithmKeys.includes(selectedAlgorithm)) {
      const isCapacityMode = demand > 0;
      
      if (isCapacityMode) {
        // Switch to capacity-constrained algorithm
        if (availableAlgorithmKeys.includes('capacity_constrained_shortest_path')) {
          console.log('Switching to capacity_constrained_shortest_path');
          setSelectedAlgorithm('capacity_constrained_shortest_path');
        } else {
          console.log('Switching to first available algorithm:', availableAlgorithmKeys[0]);
          setSelectedAlgorithm(availableAlgorithmKeys[0]);
        }
      } else {
        // Switch to standard algorithm
        if (availableAlgorithmKeys.includes('connection_shortest_path')) {
          console.log('Switching to connection_shortest_path');
          setSelectedAlgorithm('connection_shortest_path');
        } else {
          console.log('Switching to first available algorithm:', availableAlgorithmKeys[0]);
          setSelectedAlgorithm(availableAlgorithmKeys[0]);
        }
      }
    }
  }, [algorithms, selectedAlgorithm, demand]);

  // Fetch algorithms from backend
  const loadAlgorithms = async () => {
    try {
      setLoadingAlgorithms(true);
      setError('');
      
      const response = await axios.get(MTO_ALGORITHMS_API);
      console.log('Algorithm API response:', response.data);
      
      if (response.data.success) {
        // Process MTO algorithm data
        const mtoAlgorithmsList = response.data.data.mto_algorithms || [];
        const allAlgorithms = response.data.data.all_algorithms || [];
        const algorithmsInfo = response.data.data.algorithms_info || {};
        
        console.log('Available algorithms:', allAlgorithms);
        console.log('Algorithm info:', algorithmsInfo);
        
        // Use all available algorithms without filtering
        const availableAlgorithms = allAlgorithms;

        // Convert to required format
        const formattedAlgorithms = {};
        for (const algId of availableAlgorithms) {
          if (algorithmsInfo[algId]) {
            formattedAlgorithms[algId] = algorithmsInfo[algId];
          }
        }
        
        console.log('Formatted algorithms:', formattedAlgorithms);
        
        // If no algorithms from API, use default
        if (Object.keys(formattedAlgorithms).length === 0) {
          console.log('No algorithms from API, using defaults');
          setAllAlgorithms(DEFAULT_ALGORITHMS);
          setSelectedAlgorithm('connection_shortest_path');
        } else {
          setAllAlgorithms(formattedAlgorithms);
          
          // Set default algorithm to connection_shortest_path
          if (availableAlgorithms.includes('connection_shortest_path')) {
            setSelectedAlgorithm('connection_shortest_path');
          } else if (availableAlgorithms.length > 0) {
            // Fall back to first available algorithm
            const defaultAlgorithm = availableAlgorithms[0];
            setSelectedAlgorithm(defaultAlgorithm);
          }
        }
      } else {
        console.log('API error, using default algorithms');
        setAllAlgorithms(DEFAULT_ALGORITHMS);
        setSelectedAlgorithm('connection_shortest_path');
      }
    } catch (error) {
      console.error('Failed to load algorithms:', error);
      console.log('API call failed, using defaults');
      
      // Use default algorithms when API fails
      setAllAlgorithms(DEFAULT_ALGORITHMS);
      setSelectedAlgorithm('connection_shortest_path');
      setError('Failed to load algorithm data from server, using default algorithms');
    } finally {
      setLoadingAlgorithms(false);
    }
  };

  // Handle algorithm selection change
  const handleAlgorithmChange = (e) => {
    setSelectedAlgorithm(e.target.value);
  };

  return {
    algorithms,
    isLoadingAlgorithms,
    selectedAlgorithm,
    setSelectedAlgorithm,
    handleAlgorithmChange,
    error,
    loadAlgorithms
  };
}; 